import { useMutation, useQuery, useSubscription } from "@vue/apollo-composable";
import { debouncedRef } from "@vueuse/core";
import { computed, ref, watch, type Ref } from "vue";
import { Product, ProductConfig, ProductStatus, ProductSubscription } from "./models";
import {
  ASSIGN_VEHICLE_TO_PRODUCT,
  CHECK_PRODUCT_DATA_TO_LOAD,
  CHECK_SN_AVAILABILITY,
  GET_PRODUCT_CONFIG,
  INSERT_PRODUCT,
  REGISTER_PRODUCT,
  REQUEST_SUBSCRIPTION_RENEWAL,
  VALIDATE_RENEWAL_REQUEST,
  DECLINE_RENEWAL_REQUEST,
  SUBSCRIBE_PRODUCTS,
  SUBSCRIBE_PRODUCTS_COUNT,
  GET_PRODUCTS_COUNT,
  SUBSCRIBE_ALL_PRODUCTS_FOR_RENEW,
  UNREGISTER_PRODUCT,
  DECLINE_PRODUCT
} from "./queries";
import { mapProduct, mapProductConfig, mapProducts } from "./mapper";
import { useI18n } from "vue-i18n";
import { useCurrentApolloClient } from "@/plugins/apollo";

function subscribeAllProducts(defaultOrder = {}) {
  const textFilter = ref("");
  const statusFilter = ref<ProductStatus>();
  const companyFilter = ref(null);
  const subscriptionFilter = ref<ProductSubscription>();
  const columnOrders = ref(defaultOrder);

  const limit = ref(15);
  const page = ref(1);

  const whereText = computed(() =>
    textFilter.value !== ""
      ? {
        _or: [{ name: { _ilike: `%${textFilter.value}%` } }, { serial_number: { _ilike: `%${textFilter.value}%` } }],
      }
      : null
  );

  const whereStatus = computed(() =>
    statusFilter.value
      ? {
        status: { _eq: statusFilter.value },
      }
      : null
  );

  const whereCompany = computed(() =>
    companyFilter.value
      ? {
        company: { uuid: { _eq: companyFilter.value } },
      }
      : null
  );

  const whereSubscription = computed(() => {
    const now = new Date().toISOString();
    const threeMonthsFromNow = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

    // A product can have several product_subscription rows (renewal history).
    // ACTIVE and EXPIRING_SOON only need to find a row inside a given window,
    // which a single old/superseded row can never falsely satisfy on its own
    // (it fails the lower bound), so the plain relation filter is fine there.
    // EXPIRED is the one that breaks: `end_date: {_lt: now}` matches if ANY
    // row is in the past, so an old superseded row wrongly flags a product as
    // expired even though a newer row is still valid. Fixed by requiring that
    // NO row is still valid (via `_not`) instead of checking a single row's
    // end_date directly.
    switch (subscriptionFilter.value) {
      case ProductSubscription.ACTIVE:
        return {
          _or: [
            {
              products_product_subscriptions: {
                end_date: { _gt: now },
              },
            },
            {
              _not: {
                products_product_subscriptions: {},
              },
            },
            {
              products_product_subscriptions: {
                end_date: { _is_null: true },
              },
            },
          ],
        };
      case ProductSubscription.EXPIRING_SOON:
        return {
          products_product_subscriptions: {
            end_date: { _gte: now, _lte: threeMonthsFromNow },
          },
        };
      case ProductSubscription.EXPIRED:
        return {
          _and: [
            {
              products_product_subscriptions: {},
            },
            {
              _not: {
                products_product_subscriptions: {
                  end_date: { _gte: now },
                },
              },
            },
          ],
        };
      case ProductSubscription.PENDING_RENEWAL:
        return {
          products_product_subscription_requests: {
            status: { _eq: "pending" },
          },
        };
      default:
        return null;
    }
  });

  const allFilters = computed(() => [whereText.value, whereStatus.value, whereSubscription.value, whereCompany.value].filter(Boolean));

  const where = computed(() =>
    allFilters.value.length !== 0
      ? {
        _and: allFilters.value,
      }
      : {}
  );

  const offset = computed(() => (page.value - 1) * limit.value);

  const { loading: fetchLoading, result } = useSubscription(
    SUBSCRIBE_PRODUCTS,
    debouncedRef(
      computed(() => ({
        offset: offset.value,
        limit: limit.value,
        where: where.value,
        order_by: columnOrders.value,
      }))
    )
  );

  const products = computed<Product[]>(() => mapProducts(result.value?.products));

  const { result: totalResult, loading: totalLoading } = useSubscription(SUBSCRIBE_PRODUCTS_COUNT, { where: debouncedRef(where) });

  const total = computed(() => totalResult.value?.products_aggregate?.aggregate?.count ?? 0);

  watch(where, () => {
    if (page.value !== 1) {
      page.value = 1;
    }
  });

  const loading = computed(() => fetchLoading.value || totalLoading.value);

  return {
    loading,
    columnOrders,
    products,
    textFilter,
    total,
    limit,
    page,
    statusFilter,
    subscriptionFilter,
    companyFilter
  };
}

function getAllProductStatusForSelect() {
  const { t } = useI18n();

  return Object.entries(ProductStatus).map(([key, value]) => ({
    title: t(`products.status.${value}`),
    value: value,
  }));
}

function getAllProductStubscriptionForSelect() {
  const { t } = useI18n();

  return Object.entries(ProductSubscription).map(([key, value]) => ({
    title: t(`products.subscription.${value}`),
    value: value,
  }));
}

function assignProduct(productId: string, vehicle: string) {
  const { mutate, onDone, onError, loading } = useMutation(ASSIGN_VEHICLE_TO_PRODUCT);

  mutate({
    id: productId,
    vehicle_id: vehicle,
  });

  return {
    loading,
    onDone,
    onError,
  };
}

function validateProduct(product: Product, duration: string | null = null) {
  const { mutate, onDone, onError, loading } = useMutation(REGISTER_PRODUCT);

  mutate({
    id: product.id,
    company_id: product.company.uuid,
    duration
  });

  return {
    loading,
    onDone,
    onError,
  };
}

function declineProduct(product: Product) {
  const { mutate, onDone, onError, loading } = useMutation(DECLINE_PRODUCT);

  mutate({
    id: product.id,
    company_id: product.company.uuid,
  });

  return {
    loading,
    onDone,
    onError,
  };
}

function unregisterProduct(product: Product) {
  const { mutate, onDone, onError, loading } = useMutation(UNREGISTER_PRODUCT);

  mutate({
    id: product.id,
    company_id: product.company.uuid,
  });

  return {
    onDone,
    onError,
    loading,
  };
}

function insertProduct(data: any) {
  const { mutate, onDone, onError, loading } = useMutation(INSERT_PRODUCT);

  mutate({
    product: {
      vehicle_id: data.vehicle_id,
      serial_number: data.product_serial_number,
      status: ProductStatus.VALIDATION_REQUIRED,
      company_id: data.company_id,
    },
  });

  return {
    onDone,
    onError,
    loading,
  };
}

async function loadProductForm(sn: string, vehicle: string): Promise<{ companyId: string; vehicleId: string; snAvailable: boolean }> {
  if (!sn || !vehicle) {
    return {
      companyId: null,
      vehicleId: null,
      snAvailable: false,
    };
  }

  const res = await useCurrentApolloClient().query({
    query: CHECK_PRODUCT_DATA_TO_LOAD,
    variables: {
      uuid: vehicle,
    },
  });

  return {
    companyId: res.data?.vehicles_by_pk?.company?.uuid,
    vehicleId: res.data?.vehicles_by_pk?.uuid,
    snAvailable: true,
  };
}

async function checkProductAvailability(sn: string): Promise<{ isAvailable: boolean; error?: string }> {
  if (!sn) {
    return { isAvailable: false, error: null };
  }

  const res = await useCurrentApolloClient().query({
    query: CHECK_SN_AVAILABILITY,
    variables: {
      sn,
    },
  });

  const productsInDb = mapProducts(res?.data?.products);
  const productAvailabilityAtArcure = res?.data?.check_serial_number_availability;
  if (!productAvailabilityAtArcure) {
    return { isAvailable: false, error: null };
  } else if (productsInDb.length) {
    return { isAvailable: false, error: null };
  }
  return {
    isAvailable: productAvailabilityAtArcure?.isAvailable ?? false,
    error: productAvailabilityAtArcure?.error ?? null,
  };
}

async function getExpiredOrExpiringSubscriptionsCount(): Promise<number> {
  const now = new Date().toISOString();
  const in90Days = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();

  // Same EXPIRING_SOON / EXPIRED logic as whereSubscription in subscribeAllProducts:
  // a superseded row's end_date is always in the past, so it can never satisfy
  // EXPIRING_SOON's lower bound, and EXPIRED must check that NO row is still
  // valid rather than that some row's end_date has passed.
  const res = await useCurrentApolloClient().query({
    query: GET_PRODUCTS_COUNT,
    variables: {
      where: {
        _or: [
          {
            products_product_subscriptions: {
              end_date: { _gte: now, _lte: in90Days },
            },
          },
          {
            _and: [
              { products_product_subscriptions: {} },
              {
                _not: {
                  products_product_subscriptions: {
                    end_date: { _gte: now },
                  },
                },
              },
            ],
          },
        ],
      },
    },
  });

  return res?.data?.products_aggregate?.aggregate?.count ?? 0;
}

function getProductConfig(sn: Ref<string | undefined>) {
  const productConfig = ref<ProductConfig>();

  const { onResult, onError, loading } = useQuery(
    GET_PRODUCT_CONFIG,
    () => ({ sn: sn.value }),
    () => ({ enabled: !!sn.value }),
  );

  onResult((r) => {
    productConfig.value = mapProductConfig(r?.data?.get_product_config);
  });

  return {
    productConfig,
    onError,
    loading,
  };
}

function getVehicleProducts(sn: string) {
  const limit = ref(15);
  const page = ref(1);

  const offset = computed(() => (page.value - 1) * limit.value);

  const where = computed(() => {
    return { vehicle_id: { _eq: sn } };
  });

  const { loading, result } = useQuery(
    SUBSCRIBE_PRODUCTS,
    debouncedRef(
      ref({
        offset,
        limit,
        where: where,
      })
    )
  );

  const products = computed<Product[]>(() => mapProducts(result.value?.products));

  const { result: totalResult } = useQuery(SUBSCRIBE_PRODUCTS_COUNT, {
    where: debouncedRef(where),
  });
  const total = computed(() => totalResult.value?.products_aggregate?.aggregate?.count ?? 0);

  return {
    products,
    loading,
    page,
    limit,
    total,
  };
}

function validateRenewalRequest() {
  const { mutate, onDone, onError, loading } = useMutation(VALIDATE_RENEWAL_REQUEST);
  return {
    mutate: (id: string, serialNumber: string, approvedDuration: string, arcNumber: string | null, noArc: boolean) =>
      mutate({ input: { id, serialNumber, approvedDuration, arcNumber, noArc } }),
    onDone,
    onError,
    loading,
  };
}

function declineRenewalRequest() {
  const { mutate, onDone, onError, loading } = useMutation(DECLINE_RENEWAL_REQUEST);
  return {
    mutate: (id: string, reason: string | null) =>
      mutate({ input: { id, reason } }),
    onDone,
    onError,
    loading,
  };
}

function requestSubscriptionRenewal() {
  const { mutate, onDone, onError, loading } = useMutation(REQUEST_SUBSCRIPTION_RENEWAL);

  return {
    mutate: (renewals: { productId: string; serialNumber: string; duration: string }[]) =>
      mutate({ renewals }),
    onDone,
    onError,
    loading,
  };
}

function keepMostRecentPerSerialNumber(products: Product[]): Product[] {
  const mostRecentBySerialNumber = new Map<string, Product>();

  for (const product of products) {
    const existing = mostRecentBySerialNumber.get(product.serialNumber);
    if (!existing) {
      mostRecentBySerialNumber.set(product.serialNumber, product);
      continue;
    }
    const existingTime = existing.registeredAt ? new Date(existing.registeredAt).getTime() : 0;
    const currentTime = product.registeredAt ? new Date(product.registeredAt).getTime() : 0;
    if (currentTime > existingTime) {
      mostRecentBySerialNumber.set(product.serialNumber, product);
    }
  }

  return Array.from(mostRecentBySerialNumber.values());
}

function subscribeAllProductsForRenew() {
  const where = {
    status: { _in: [ProductStatus.REGISTERED, ProductStatus.UNREGISTERED] },
  };
  const { loading, result } = useSubscription(SUBSCRIBE_ALL_PRODUCTS_FOR_RENEW, { where });
  const products = computed<Product[]>(() => keepMostRecentPerSerialNumber(mapProducts(result.value?.products)));
  return { products, loading };
}

function subscribeAllAvailableProductsForCompany(defaultOrder = {}) {
  const companyFilter = ref();
  const columnOrders = ref(defaultOrder);

  const whereCompany = computed(() =>
    companyFilter.value
      ? {
        company_id: { _eq: companyFilter.value },
      }
      : null
  );

  const whereEmptyVehicle = computed(() => {
    return {
      vehicle_id: { _is_null: true },
    };
  });

  const whereStatus = computed(() => ({
    status: { _in: [ProductStatus.REGISTERED, ProductStatus.VALIDATION_REQUIRED] },
  }));

  const allFilters = computed(() => [whereCompany.value, whereEmptyVehicle.value, whereStatus.value].filter(Boolean));

  const where = computed(() =>
    allFilters.value.length !== 0
      ? {
        _and: allFilters.value,
      }
      : {}
  );

  const { loading, result } = useSubscription(
    SUBSCRIBE_PRODUCTS,
    debouncedRef(
      ref({
        where: where,
        order_by: columnOrders,
      })
    )
  );

  const products = computed<Product[]>(() => mapProducts(result.value?.products));

  return {
    loading,
    columnOrders,
    products,
    companyFilter,
  };
}

export {
  subscribeAllProducts,
  subscribeAllProductsForRenew,
  requestSubscriptionRenewal,
  validateRenewalRequest,
  declineRenewalRequest,
  getAllProductStatusForSelect,
  getAllProductStubscriptionForSelect,
  assignProduct,
  validateProduct,
  declineProduct,
  unregisterProduct,
  loadProductForm,
  checkProductAvailability,
  insertProduct,
  getProductConfig,
  getVehicleProducts,
  subscribeAllAvailableProductsForCompany,
  getExpiredOrExpiringSubscriptionsCount,
};
